import type { SchoolSummary } from '@school-erp/types';
import {
  SETTINGS_REGISTRY,
  type SettingView,
  formatNumber,
  isSettingKey,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { t, type MessageKey } from '../../i18n';

export function SettingsPage() {
  const { can, user } = useAuth();
  const [schoolId, setSchoolId] = useState<string>('');

  const schools = useQuery({
    queryKey: ['schools'],
    queryFn: () => api<SchoolSummary[]>('/schools'),
  });
  // Organization-level settings belong to an organization; a platform super administrator has none, so starts at a school.
  const orgLevel = can('settings.view') && !!user?.organizationId;
  // Users with only school-scoped grants cannot view organization level; start at their first school.
  const scope = schoolId || (orgLevel ? '' : (schools.data?.[0]?.id ?? ''));

  const settings = useQuery({
    queryKey: ['settings', scope],
    queryFn: () => api<SettingView[]>('/settings', { query: { schoolId: scope || undefined } }),
    enabled: orgLevel || !!scope,
  });

  const groups = new Map<string, SettingView[]>();
  for (const setting of settings.data ?? []) {
    groups.set(setting.group, [...(groups.get(setting.group) ?? []), setting]);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">{t('settings.title')}</h1>

      <div>
        <label htmlFor="settings-scope" className="block text-sm font-medium">
          {t('settings.scope')}
        </label>
        <select
          id="settings-scope"
          value={scope}
          onChange={(e) => setSchoolId(e.target.value)}
          className="mt-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          {orgLevel && <option value="">{t('settings.scopeOrg')}</option>}
          {schools.data?.map((school) => (
            <option key={school.id} value={school.id}>
              {school.name}
            </option>
          ))}
        </select>
      </div>

      {settings.isPending && settings.fetchStatus !== 'idle' && (
        <p role="status">{t('common.loading')}</p>
      )}
      {settings.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}

      {[...groups].map(([group, items]) => (
        <section
          key={group}
          aria-labelledby={`group-${group.replace(/\s+/g, '-')}`}
          className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        >
          <h2 id={`group-${group.replace(/\s+/g, '-')}`} className="text-base font-semibold">
            {group}
          </h2>
          {items.map((setting) => (
            <SettingRow
              key={`${scope}:${setting.key}`}
              setting={setting}
              schoolId={scope || null}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function SettingRow({ setting, schoolId }: { setting: SettingView; schoolId: string | null }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const editable = can('settings.update', schoolId ?? undefined);
  const [value, setValue] = useState<unknown>(setting.value);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const inputId = `setting-${setting.key}`;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['settings'] });
  const save = useMutation({
    mutationFn: () => api(`/settings/${setting.key}`, { method: 'PUT', body: { value, schoolId } }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void refresh();
    },
    onError: (e) =>
      setMessage({
        kind: 'error',
        text: e instanceof ApiError ? e.message : t('common.actionError'),
      }),
  });
  const reset = useMutation({
    mutationFn: () => api(`/settings/${setting.key}`, { method: 'DELETE', query: { schoolId } }),
    onSuccess: () => {
      setMessage(null);
      void refresh();
    },
    onError: (e) =>
      setMessage({
        kind: 'error',
        text: e instanceof ApiError ? e.message : t('common.actionError'),
      }),
  });

  const definition = isSettingKey(setting.key) ? SETTINGS_REGISTRY[setting.key] : undefined;
  const preview =
    setting.key.startsWith('numbering.') && typeof value === 'string'
      ? (() => {
          try {
            return formatNumber(value, 42, new Date());
          } catch {
            return null;
          }
        })()
      : null;
  const overridden = setting.source === (schoolId ? 'school' : 'organization');

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-sm font-medium">
        {setting.label}
      </label>
      <p className="text-xs text-slate-600">{setting.description}</p>
      <div className="flex flex-wrap items-center gap-2">
        {setting.kind === 'boolean' ? (
          <input
            id={inputId}
            type="checkbox"
            disabled={!editable}
            checked={value === true}
            onChange={(e) => setValue(e.target.checked)}
          />
        ) : setting.kind === 'number' ? (
          <input
            id={inputId}
            type="number"
            disabled={!editable}
            value={String(value ?? '')}
            onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-28 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        ) : setting.kind === 'weekdays' ? (
          <fieldset id={inputId} className="flex gap-2" disabled={!editable}>
            <legend className="sr-only">{setting.label}</legend>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <label key={day} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(day)}
                  onChange={(e) => {
                    const current = Array.isArray(value) ? (value as number[]) : [];
                    setValue(
                      e.target.checked
                        ? [...current, day].sort()
                        : current.filter((d) => d !== day),
                    );
                  }}
                />
                {t(`settings.weekday.${day}` as MessageKey)}
              </label>
            ))}
          </fieldset>
        ) : (
          <input
            id={inputId}
            disabled={!editable}
            value={String(value ?? '')}
            onChange={(e) => setValue(e.target.value)}
            className="w-72 max-w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )}
        {editable && (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="rounded bg-brand-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        )}
        {editable && overridden && (
          <button
            type="button"
            onClick={() => reset.mutate()}
            className="rounded border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
          >
            {t('settings.reset')}
          </button>
        )}
        <span className="text-xs text-slate-500">
          {t(`settings.source.${setting.source}` as MessageKey)}
        </span>
      </div>
      {preview && (
        <p className="text-xs text-slate-600">{t('settings.preview', { value: preview })}</p>
      )}
      {definition === undefined && <p className="text-xs text-amber-700">Unknown setting</p>}
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-xs text-red-700' : 'text-xs text-green-800'}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
